import React, { useState, useEffect } from "react";
import styles from "./Row.module.sass";
import cn from "classnames";
import Checkbox from "../../../../../../components/Checkbox";
import Control from "./Control";
import Actions from "../../../../../../components/Actions";
import Icon from "../../../../../../components/Icon";
import ModalSignAttendance from "../../../../../../components/ModalSignAttendance/ModalSignAttendance";
import { updateAttendance } from "../../../../../../services/attendances.service";

const Row = ({ item, value, onChange, onEdit, onDelete, onSendSignatureInvitation, onAttendanceUpdate, session }) => {
    const [showSignModal, setShowSignModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(item);
    const [visibleModalSchedule, setVisibleModalSchedule] = useState(false);
    
    
    // Actions dynamiques basées sur le statut de l'attendance
    const getActions = () => {
        return [
            {
                title: "Signature manuelle",
                action: () => handleManualSign(),
            },
            {
                title: "Envoyer invitation signature",
                action: () => onSendSignatureInvitation?.([currentItem.id]),
            },
            {
                title: "Marquer absent",
                action: () => handleMarkAbsent(),
            },
            {
                title: "Départ anticipé",
                action: () => handleEarlyDeparture(),
            },
        ];
    };

    const actions = getActions();
    
    // Écouter les mises à jour de statut d'attendance
    useEffect(() => {
        const handleAttendanceUpdate = (event) => {
            const { attendanceId, newStatus, hasSigned } = event.detail;
            
            // Si c'est notre attendance qui a été mise à jour
            if (attendanceId === currentItem.id) {
                setCurrentItem(prev => ({
                    ...prev,
                    status: newStatus,
                    signature_file: hasSigned ? 'signed' : prev.signature_file
                }));
            }
        };

        window.addEventListener('attendanceStatusUpdated', handleAttendanceUpdate);
        
        return () => {
            window.removeEventListener('attendanceStatusUpdated', handleAttendanceUpdate);
        };
    }, [currentItem.id]);

    // Mettre à jour l'item local quand les props changent
    useEffect(() => {
        setCurrentItem(item);
    }, [item]);
    
    // Récupérer les informations du participant depuis l'expansion
    const participant = currentItem.expand?.participant_id;
    
    // Fonction pour formater le statut de présence
    const getPresenceStatus = (status) => {
        const statusLower = status?.toLowerCase();
        
        if (statusLower === 'présent' || statusLower === 'present') {
            return (
                <div className={cn('status-type', 'status-green-dark')}>
                    Présent
                </div>
            );
        } else if (statusLower === 'absent') {
            return (
                <div className={cn('status-type', 'status-red-dark')}>
                    Absent
                </div>
            );
        } else if (statusLower === 'en retard') {
            return (
                <div className={cn('status-type', 'status-yellow-dark')}>
                    En retard
                </div>
            );
        } else {
            return (
                <div className={cn('status-type', 'status-gray-dark')}>
                    {status || 'Non défini'}
                </div>
            );
        }
    };

    // Gérer la signature manuelle
    const handleManualSign = () => {
        setShowSignModal(true);
    };

    const handleSignatureSuccess = (attendanceId) => {
        setShowSignModal(false);
        // Notifier le parent pour rafraîchir les données
        if (onAttendanceUpdate) {
            onAttendanceUpdate(attendanceId);
        }
    };

    // Marquer absent
    const handleMarkAbsent = async () => {
        try {
            await updateAttendance(currentItem.id, {
                status: 'Absent',
                comments: 'Marqué absent par le professeur'
            });
            
            // Mettre à jour l'état local
            setCurrentItem(prev => ({
                ...prev,
                status: 'Absent'
            }));
            
            // Notifier le parent pour rafraîchir les données
            if (onAttendanceUpdate) {
                onAttendanceUpdate(currentItem.id);
            }
        } catch (error) {
            console.error("Erreur lors du marquage absent:", error);
        }
    };

    // Marquer départ anticipé
    const handleEarlyDeparture = async () => {
        try {
            const currentTime = new Date().toLocaleTimeString('fr-FR', { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
            
            await updateAttendance(currentItem.id, {
                status: "Départ anticipé",
                comments: `Départ anticipé à ${currentTime} - Marqué par le professeur`
            });
            
            // Mettre à jour l'état local
            setCurrentItem(prev => ({
                ...prev,
                status: 'Départ anticipé'
            }));
            
            // Notifier le parent pour rafraîchir les données
            if (onAttendanceUpdate) {
                onAttendanceUpdate(currentItem.id);
            }
        } catch (error) {
            console.error("Erreur lors du marquage départ anticipé:", error);
        }
    };

    return (
        <>
            <div className={styles.row}>
                <div className={styles.col}>
                    <Checkbox
                        className={styles.checkbox}
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                </div>
                <div className={styles.col}>
                    <div className={styles.item}>
                        <div className={styles.avatar}>
                            <img src="/images/content/user_logo.png" alt="Étudiant" />
                        </div>
                        <div className={styles.details}>
                            <div className={styles.product}>
                                {participant ? `${participant.first_name} ${participant.last_name}` : 'Participant inconnu'}
                            </div>
                            <div className={styles.date}>
                                <Icon name="calendar" size="16" /> 
                                {item.created ? new Date(item.created).toLocaleDateString('fr-FR') : 'Date inconnue'}
                            </div>
                        </div>
                    </div>
                    <Actions
                        className={styles.actions}
                        classActionsHead={styles.actionsHead}
                        items={actions}
                        small
                    />
                </div>
                <div className={styles.col}>
                    <div className={styles.contact}>
                        {participant?.email || 'Email non spécifié'}
                    </div>
                </div>
                <div className={styles.col}>
                    {getPresenceStatus(currentItem.status)}
                </div>
                <div className={styles.col}>
                <Control
                        className={styles.control}
                        actions={actions}
                    />
                </div>
            </div>

            {showSignModal && (
                <ModalSignAttendance
                    visible={showSignModal}
                    onClose={() => setShowSignModal(false)}
                    attendance={currentItem}
                    session={session}
                    onSignatureSuccess={handleSignatureSuccess}
                />
            )}
        </>
    );
};

export default Row;
